import { Link } from "react-router-dom";
import { Check, Phone, Ticket, MapPin, Sparkles, ChevronRight, Clock, Wallet } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLangPrefix, withLangPrefix } from "../hooks/useLangPrefix";

/**
 * CoPilotagePage — private-URL only offer landing page (not in the main nav).
 * Presents the "Le Co-Pilotage" service (3/6/12 month plans) with clear pricing,
 * inclusions, and a CTA that funnels to /devis with the co-pilotage context.
 */
const PLANS = [
  {
    key: "3m",
    label: "3 mois",
    duration_months: 3,
    hourly: 160,
    highlight: false,
    tagline: "Coup d'accélérateur",
    subtitle: "Idéal pour un chantier ou un lancement d'offre.",
    perks: [
      "Support téléphonique dédié",
      "Support par ticket (échanges asynchrones)",
      "Jusqu'à 4 h d'accompagnement / mois",
      "Évaluation & organisation de chantier",
      "Réponses techniques prioritaires",
    ],
  },
  {
    key: "6m",
    label: "6 mois",
    duration_months: 6,
    hourly: 140,
    highlight: true,
    tagline: "Le plus choisi",
    subtitle: "Le meilleur ratio pour structurer votre activité.",
    perks: [
      "Support téléphonique dédié",
      "Support par ticket (échanges asynchrones)",
      "Jusqu'à 4 h d'accompagnement / mois",
      "Suivi d'organisation & de gestion de chantier",
      "Bilan mi-parcours & feuille de route",
    ],
  },
  {
    key: "12m",
    label: "12 mois",
    duration_months: 12,
    hourly: 120,
    highlight: false,
    tagline: "Accompagnement long",
    subtitle: "Pour un partenariat continu sur toute une saison.",
    perks: [
      "Support téléphonique dédié",
      "Support par ticket (échanges asynchrones)",
      "Jusqu'à 4 h d'accompagnement / mois",
      "Support sur site & à distance selon besoins",
      "Point stratégique trimestriel",
    ],
  },
];

const formatEUR = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export const CoPilotagePage = () => {
  const { lang } = useLangPrefix();

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="copilotage-page">
      {/* Hero — dark navy panel */}
      <section className="relative overflow-hidden bg-[#1F2A4A] text-[#FAF7F0] pt-36 pb-20 md:pt-44 md:pb-32">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#C9B896] mb-4 font-semibold inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
            Accompagnement premium · sur demande
          </p>
          <h1 className="font-light text-3xl md:text-5xl lg:text-6xl leading-[1.1] mb-6 max-w-3xl text-[#FAF7F0]">
            Le <span className="font-serif italic text-[#C9B896]">Co-Pilotage</span>
            <br />
            Sellerie&nbsp;Académie.
          </h1>
          <p className="text-[#E8DCC8]/90 font-light text-base md:text-xl leading-relaxed max-w-2xl">
            Un support technique sur site et à distance pour vous accompagner
            dans l'évaluation, l'organisation et la gestion de vos chantiers.
            Réservé aux entrepreneurs qui veulent structurer leur atelier avec
            un partenaire expérimenté.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#offres" className="inline-flex items-center gap-2 bg-[#C9B896] hover:bg-[#E0D4BA] text-[#1F2A4A] font-semibold rounded-full px-8 h-12 text-sm uppercase tracking-[0.15em] transition-colors shadow-lg shadow-[#C9B896]/25">
              Voir les forfaits
              <ChevronRight className="w-4 h-4" />
            </a>
            <Link
              to={withLangPrefix("/contact", lang)}
              className="inline-flex items-center gap-2 border border-[#C9B896]/50 hover:border-[#C9B896] text-[#FAF7F0] rounded-full px-8 h-12 text-sm uppercase tracking-[0.15em] transition-colors"
              data-testid="copilotage-contact-cta"
            >
              Contactez-nous
            </Link>
          </div>
        </div>
      </section>

      {/* Ce que vous obtenez — features grid */}
      <section className="py-16 md:py-24 border-b border-[#1F2A4A]/8">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-3 font-semibold">Ce que vous obtenez</p>
          <h2 className="font-light text-3xl md:text-5xl text-[#1F2A4A] leading-tight mb-12 max-w-3xl">
            Un vrai partenaire, <span className="font-serif italic">pas juste une hotline.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { Icon: Phone, title: "Support téléphonique", body: "Accès direct à notre expertise sellerie. Vous n'êtes plus seul face à un chantier complexe ou une matière inconnue." },
              { Icon: Ticket, title: "Support sous ticket", body: "Envoyez photos, plans, mesures — recevez une analyse technique argumentée sous 48 h ouvrées." },
              { Icon: MapPin, title: "Support sur site & à distance", body: "Nous évaluons, organisons et pilotons vos chantiers en présentiel (déplacement) ou en visio-atelier." },
            ].map(({ Icon, title, body }, i) => (
              <div key={i} className="border-t border-[#1F2A4A]/12 pt-6" data-testid={`copilotage-feature-${i}`}>
                <Icon className="w-6 h-6 text-[#C9B896] mb-3" strokeWidth={1.75} />
                <h3 className="text-xs uppercase tracking-[0.22em] text-[#1F2A4A] font-semibold mb-3">{title}</h3>
                <p className="text-[#4A5568] font-light leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offres — 3 pricing cards */}
      <section id="offres" className="py-16 md:py-24 bg-[#F4EFE3]/60">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="mb-12 flex flex-wrap items-end gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-3 font-semibold">Trois forfaits</p>
              <h2 className="font-light text-3xl md:text-5xl text-[#1F2A4A] leading-tight max-w-3xl">
                Choisissez votre <span className="font-serif italic">durée d'accompagnement.</span>
              </h2>
            </div>
            <p className="text-sm text-[#4A5568] font-light max-w-md ml-auto">
              Chaque forfait inclut <b className="text-[#1F2A4A]">jusqu'à 4 heures d'accompagnement par mois</b> (téléphone + tickets), avec possibilité d'heures additionnelles au tarif du forfait.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {PLANS.map((plan) => (
              <PlanCard key={plan.key} plan={plan} lang={lang} />
            ))}
          </div>

          <p className="mt-10 text-center text-xs text-[#4A5568] italic">
            Tous les tarifs sont exprimés HT. TVA non applicable — article 293 B du CGI.
          </p>
        </div>
      </section>

      {/* FAQ courte */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-3 font-semibold text-center">Questions fréquentes</p>
          <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A] leading-tight text-center mb-10">
            Ce qu'il faut <span className="font-serif italic">savoir avant de démarrer.</span>
          </h2>

          <div className="space-y-4">
            {[
              { q: "Comment sont comptabilisées les heures ?", a: "Chaque appel téléphonique et chaque ticket traité sont chronométrés à la minute et déduits de votre enveloppe mensuelle (4 h). Un récapitulatif vous est envoyé chaque mois." },
              { q: "Puis-je basculer d'un forfait à l'autre ?", a: "Oui, à chaque échéance vous pouvez passer sur un forfait plus long (avec un tarif horaire plus avantageux) sans coût de transition." },
              { q: "Les heures non consommées sont-elles reportées ?", a: "Elles ne sont pas reportées automatiquement d'un mois sur l'autre. Sur demande, nous étudions un aménagement en cas de contexte exceptionnel." },
              { q: "Y a-t-il un engagement ?", a: "Vous choisissez la durée du forfait à la signature (3, 6 ou 12 mois). Aucune tacite reconduction : à l'issue, vous décidez librement de renouveler ou non." },
            ].map((it, i) => (
              <details key={i} className="rounded-xl border border-[#1F2A4A]/12 bg-white group open:bg-[#FAF7F0]" data-testid={`copilotage-faq-${i}`}>
                <summary className="list-none cursor-pointer flex items-start justify-between gap-4 px-5 py-4">
                  <span className="font-medium text-[#1F2A4A]">{it.q}</span>
                  <ChevronRight className="w-4 h-4 text-[#C9B896] shrink-0 mt-1 transition-transform group-open:rotate-90" />
                </summary>
                <p className="px-5 pb-5 pt-1 text-sm text-[#4A5568] font-light leading-relaxed">{it.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-[#1F2A4A] text-[#FAF7F0] text-center">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-light text-3xl md:text-4xl lg:text-5xl leading-tight mb-5 text-[#FAF7F0]">
            Prêt à être <span className="font-serif italic text-[#C9B896]">co-piloté&nbsp;?</span>
          </h2>
          <p className="text-[#E8DCC8]/85 font-light text-base md:text-lg leading-relaxed mb-8 max-w-xl mx-auto">
            Contactez-nous pour un premier échange offert. Nous cadrerons ensemble le forfait le plus adapté à votre activité.
          </p>
          <Link
            to={withLangPrefix("/contact", lang)}
            className="inline-flex items-center gap-2 bg-[#C9B896] hover:bg-[#E0D4BA] text-[#1F2A4A] font-semibold rounded-full px-8 h-12 text-sm uppercase tracking-[0.15em] transition-colors shadow-lg shadow-[#C9B896]/25"
            data-testid="copilotage-final-cta"
          >
            Démarrer une conversation
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

const PlanCard = ({ plan, lang }) => {
  const monthlyTotal = plan.hourly * 4;
  const engagementLabel = `${plan.duration_months} mois d'engagement`;

  return (
    <div
      className={`relative rounded-2xl p-7 md:p-8 flex flex-col ${
        plan.highlight
          ? "bg-[#1F2A4A] text-[#FAF7F0] border-2 border-[#C9B896] shadow-2xl shadow-[#1F2A4A]/25 md:-translate-y-3"
          : "bg-white text-[#1F2A4A] border border-[#1F2A4A]/12 shadow-sm"
      }`}
      data-testid={`copilotage-plan-${plan.key}`}
    >
      {plan.highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9B896] text-[#1F2A4A] text-[10px] uppercase tracking-[0.22em] font-semibold rounded-full px-4 py-1 shadow-md">
          {plan.tagline}
        </span>
      )}
      {!plan.highlight && (
        <p className="text-[10px] uppercase tracking-[0.25em] font-semibold mb-3 text-[#C9B896]">
          {plan.tagline}
        </p>
      )}

      {/* Duration — big, no-scan-needed */}
      <div className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 mb-4 w-fit ${plan.highlight ? "bg-[#C9B896] text-[#1F2A4A]" : "bg-[#1F2A4A] text-[#C9B896]"}`}>
        <Clock className="w-3.5 h-3.5" strokeWidth={2.2} />
        <span className="text-xs uppercase tracking-[0.2em] font-bold">
          {engagementLabel}
        </span>
      </div>

      <h3 className="text-3xl md:text-4xl font-light leading-tight mb-2">
        Co-Pilotage <br className="hidden md:inline" />
        <span className="font-serif italic">{plan.label}</span>
      </h3>
      <p className={`text-sm font-light mb-6 ${plan.highlight ? "text-[#E8DCC8]/80" : "text-[#4A5568]"}`}>
        {plan.subtitle}
      </p>

      {/* Pricing block — dominant, with monthly total spelled out */}
      <div className={`rounded-xl px-5 py-5 mb-6 border ${plan.highlight ? "bg-white/[0.08] border-white/12" : "bg-[#FAF7F0] border-[#1F2A4A]/8"}`}>
        <div className="flex items-baseline gap-1.5 mb-1">
          <Wallet className="w-4 h-4 text-[#C9B896]" strokeWidth={2} />
          <span className={`text-3xl md:text-4xl font-semibold ${plan.highlight ? "text-[#FAF7F0]" : "text-[#1F2A4A]"}`}>
            {formatEUR(plan.hourly)}
          </span>
          <span className={`text-sm ${plan.highlight ? "text-[#E8DCC8]/75" : "text-[#4A5568]"}`}>HT / heure</span>
        </div>

        <div className={`h-px my-3 ${plan.highlight ? "bg-white/12" : "bg-[#1F2A4A]/10"}`} />

        <p className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-1 ${plan.highlight ? "text-[#C9B896]" : "text-[#C9B896]"}`}>
          Forfait mensuel plein (4 h)
        </p>
        <p className={`text-2xl md:text-3xl font-light ${plan.highlight ? "text-[#FAF7F0]" : "text-[#1F2A4A]"}`}>
          <b className="font-semibold">{formatEUR(monthlyTotal)}</b>
          <span className={`text-sm ml-1.5 ${plan.highlight ? "text-[#E8DCC8]/75" : "text-[#4A5568]"}`}>HT / mois</span>
        </p>
        <p className={`text-[11px] mt-1 ${plan.highlight ? "text-[#E8DCC8]/60" : "text-[#4A5568]/70"}`}>
          Soit {formatEUR(monthlyTotal * plan.duration_months)} HT sur la durée totale du forfait
        </p>
      </div>

      <ul className="space-y-2.5 mb-8 flex-1">
        {plan.perks.map((perk, i) => (
          <li key={i} className={`flex items-start gap-2 text-sm ${plan.highlight ? "text-[#E8DCC8]/90" : "text-[#1F2A4A]/85"} font-light leading-snug`}>
            <Check className="w-4 h-4 shrink-0 mt-0.5 text-[#C9B896]" strokeWidth={2.2} />
            {perk}
          </li>
        ))}
      </ul>

      <Link
        to={withLangPrefix("/contact", lang)}
        className={`inline-flex items-center justify-center gap-1.5 rounded-full h-11 text-sm uppercase tracking-[0.15em] font-semibold transition-colors ${
          plan.highlight
            ? "bg-[#C9B896] hover:bg-[#E0D4BA] text-[#1F2A4A]"
            : "bg-[#1F2A4A] hover:bg-[#141C2E] text-[#FAF7F0]"
        }`}
        data-testid={`copilotage-plan-cta-${plan.key}`}
      >
        Demander ce forfait
        <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  );
};

export default CoPilotagePage;
